<script>
import { mapActions, mapState } from 'vuex';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import {
  EditOutlined,
  DeleteOutlined,
  ApiOutlined,
  BgColorsOutlined,
  EllipsisOutlined,
} from '@ant-design/icons-vue';
import CompanyModal from '@/components/modals/Company-modal.vue';
import EditColorsModal from '@/components/pageContent/EditColorsModal.vue';
import dayjs from 'dayjs';
import pageUrls from '@/router/pageUrls';
import { mapColorsToFullObject } from '@/utils/colorUtils';
import columns from './columns';

const { companiesPage } = pageUrls;
const b = bem('companies-page');

export default {
  components: {
    PageHeader,
    EditOutlined,
    DeleteOutlined,
    ApiOutlined,
    BgColorsOutlined,
    EllipsisOutlined,
    CompanyModal,
    EditColorsModal,
  },
  data() {
    return {
      title: 'PODs',
      columns: columns(),
      limit: 10,
      offset: 0,
      showModal: false,
      selectedCompany: null,
      isEditColorsModalVisible: false,
      selectedCompanyColors: {
        color1: '',
        color2: '',
        color3: '',
      },
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompaniesAction: 'companies/getCompanies',
      deleteCompanyAction: 'companies/deleteCompany',
      getCompanyColors: 'companies/getCompanyColors',
      saveCompanyColors: 'companies/saveCompanyColors',
    }),
    getCompanies() {
      this.getCompaniesAction({ limit: this.limit, offset: this.offset });
    },
    setPagination(page, pageSize) {
      this.offset = (page - 1) * pageSize;
      this.limit = pageSize;
    },
    openCreateModal() {
      this.selectedCompany = null;
      this.showModal = true;
    },
    openEditModal(company) {
      this.selectedCompany = company;
      this.showModal = true;
    },
    deleteCompany(id) {
      this.deleteCompanyAction({ id, limit: this.limit, offset: this.offset });
    },
    formatDate(date) {
      return date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-';
    },
    navigateToApiConfig(companyId) {
      this.$router.push(`${companiesPage}/${companyId}/api-config`);
    },
    navigateToCompany(companyId) {
      this.$router.push(`${companiesPage}/${companyId}`);
    },
    async handleEditColors(company) {
      this.selectedCompany = company;
      this.selectedCompanyColors = {
        color1: '',
        color2: '',
        color3: '',
      };
      try {
        const colors = await this.getCompanyColors(company.id);
        if (colors && colors.length > 0) {
          const colorMap = mapColorsToFullObject(colors);
          this.selectedCompanyColors = {
            color1: colorMap.color1?.hex || '',
            color2: colorMap.color2?.hex || '',
            color3: colorMap.color3?.hex || '',
            ids: {
              color1: colorMap.color1?.id,
              color2: colorMap.color2?.id,
              color3: colorMap.color3?.id,
            },
          };
        }
      } catch {
        // Colors not found, using defaults
      }
      this.isEditColorsModalVisible = true;
    },
    handleCloseEditColorsModal() {
      this.isEditColorsModalVisible = false;
    },
    async handleSaveColors(colorsData) {
      try {
        await this.saveCompanyColors({
          companyId: this.selectedCompany.id,
          colors: colorsData,
          existingIds: this.selectedCompanyColors.ids,
        });
        this.isEditColorsModalVisible = false;
      } catch {
        // Error handled by store action
      }
    },
  },
  mounted() {
    this.getCompanies();
  },
  computed: {
    ...mapState({
      companies: (state) => state.companies.companies,
      companiesTotal: (state) => state.companies.companiesTotal,
      isCompaniesLoaded: (state) => state.companies.isCompaniesLoaded,
    }),
  },
  watch: {
    offset() {
      this.getCompanies();
    },
    limit() {
      this.getCompanies();
    },
  },
};
</script>

<template>
  <PageHeader :title="title" />
  <div :class="b()">
    <div :class="b('top-wrapper')">
      <a-button type="primary" html-type="button" @click="openCreateModal">
        Create New Company
      </a-button>
    </div>

    <CompanyModal
      v-if="showModal"
      v-model:show="showModal"
      :company="selectedCompany"
      :limit="limit"
      :offset="offset"
    />

    <EditColorsModal
      :visible="isEditColorsModalVisible"
      :initial-colors="selectedCompanyColors"
      @close="handleCloseEditColorsModal"
      @save="handleSaveColors"
    />

    <div :class="b('table')">
      <a-table
        :columns="columns"
        :data-source="companies"
        :loading="!isCompaniesLoaded"
        :pagination="{ pageSize: limit, total: companiesTotal, onChange: setPagination }"
        :row-key="(record) => record.id"
        size="middle"
        :custom-row="(record) => ({
          onClick: (event) => {
            const isActionButton = event.target.closest('button, .ant-btn, .ant-dropdown');
            if (!isActionButton) {
              navigateToCompany(record.id);
            }
          },
          style: { cursor: 'pointer' }
        })"
      >
        <template #bodyCell="{ column, record }">
          <!-- Company: name + ID badge -->
          <template v-if="column.key === 'company'">
            <div :class="b('company-cell')">
              <span :class="b('company-name')">{{ record.displayName }}</span>
              <a-tag size="small" :class="b('company-id')">#{{ record.id }}</a-tag>
            </div>
          </template>
          <template v-else-if="column.dataIndex === 'addedAt'">
            {{ formatDate(record.addedAt) }}
          </template>
          <!-- Actions dropdown -->
          <template v-else-if="column.key === 'actions'">
            <a-dropdown :trigger="['click']">
              <a-button type="text" size="small" @click.stop>
                <EllipsisOutlined style="font-size: 18px;" />
              </a-button>
              <template #overlay>
                <a-menu>
                  <a-menu-item key="edit" @click="openEditModal(record)">
                    <EditOutlined /> Edit
                  </a-menu-item>
                  <a-menu-item key="colors" @click="handleEditColors(record)">
                    <BgColorsOutlined /> Company colors
                  </a-menu-item>
                  <a-menu-item key="api" @click="navigateToApiConfig(record.id)">
                    <ApiOutlined /> API Config
                  </a-menu-item>
                  <a-menu-divider />
                  <a-popconfirm
                    title="Delete this company?"
                    @confirm="deleteCompany(record.id)"
                  >
                    <a-menu-item key="delete" danger>
                      <DeleteOutlined /> Delete
                    </a-menu-item>
                  </a-popconfirm>
                </a-menu>
              </template>
            </a-dropdown>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<style scoped lang="scss">
.companies-page {
  padding: 0 24px 24px;

  &__top-wrapper {
    display: flex;
    justify-content: flex-end;
    padding: 16px 0;
  }

  &__table {
    :deep(.ant-table) {
      background: var(--Background-Main);
      border-radius: 8px;
      border: 1px solid var(--Border-Light);
    }

    :deep(.ant-table-thead > tr > th) {
      background: var(--Background-Secondary);
      color: var(--Text-Secondary);
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
  }

  &__company-cell {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__company-name {
    font-weight: 600;
    color: var(--Text-Primary);
  }

  &__company-id {
    font-size: 11px;
  }
}
</style>
